import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { trpc } from "@/lib/trpc";
import { ArrowLeft, AlertCircle, CheckCircle2, Lock, Pencil, ChevronsUpDown, Check } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { toast as sonnerToast } from "sonner";
import { cn } from "@/lib/utils";
import { usePageTitle } from "@/hooks/usePageTitle";

// ─── Helper: remove accents for search ───
function removeAccents(str: string): string {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export default function QuestionarioNova() {
  usePageTitle("Nova Campanha");
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const searchParams = new URLSearchParams(useSearch());
  
  // Parâmetros vindos da URL (pré-preenchimento dos filtros)
  const channelIdFromUrl = searchParams.get("channelId");
  const clientIdFromUrl = searchParams.get("clientId");

  // Estados do formulário
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(
    channelIdFromUrl ? Number(channelIdFromUrl) : null
  );
  const [selectedClientId, setSelectedClientId] = useState<number | null>(
    clientIdFromUrl ? Number(clientIdFromUrl) : null
  );
  const [kMin, setKMin] = useState(1);
  const [endsAt, setEndsAt] = useState("");
  const [allowMultipleResponsesPerDevice, setAllowMultipleResponsesPerDevice] = useState(false);

  // Estados de "travamento" dos campos pré-preenchidos
  const [channelLocked, setChannelLocked] = useState(!!channelIdFromUrl);
  const [clientLocked, setClientLocked] = useState(!!clientIdFromUrl);

  // Popover open states
  const [channelOpen, setChannelOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);

  // Busca de cliente (server-side, com debounce) — evita depender de um lote
  // fixo (limit) carregado uma única vez, que deixava clientes "fora da
  // janela" impossíveis de encontrar (PJC-34).
  const [clientSearchInput, setClientSearchInput] = useState("");
  const [clientSearchDebounced, setClientSearchDebounced] = useState("");
  const [selectedClientName, setSelectedClientName] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setClientSearchDebounced(clientSearchInput), 300);
    return () => clearTimeout(timer);
  }, [clientSearchInput]);

  const isAdminSalvia = user?.role === "admin" || user?.role === "salvia_operation_admin";
  const isAdminCanal = user?.role === "channel_admin" || user?.role === "channel_admin_multi";
  const isStructuralChannel = user?.role === "channel_admin" || user?.role === "channel_admin_multi" || user?.role === "channel_consultant";

  // Papel estrutural de Canal tem acesso somente de consulta às campanhas existentes.
  if (isStructuralChannel) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
            <h2 className="text-xl font-semibold text-muted-foreground">Acesso Restrito</h2>
            <p className="text-sm text-muted-foreground">Seu perfil de Canal possui acesso somente de consulta no módulo de Questionários.</p>
            <Button variant="outline" onClick={() => setLocation("/home/canal")}>Voltar ao Início</Button>
          </div>
        </div>
      </div>
    );
  }

  // Listar canais (somente Admin Sálvia)
  const { data: channels, isLoading: loadingChannels } = trpc.channels.list.useQuery(undefined, {
    enabled: isAdminSalvia,
  });

  // Listar clientes (busca server-side via `search`, não apenas o lote inicial)
  const { data: clientsResponse, isLoading: loadingClients } = trpc.clients.list.useQuery(
    {
      channelId: isAdminSalvia ? selectedChannelId ?? undefined : undefined,
      status: "active",
      search: clientSearchDebounced || undefined,
      limit: 100,
      offset: 0,
    },
    {
      enabled: isAdminCanal || (isAdminSalvia && !!selectedChannelId) || !isAdminSalvia,
    }
  );

  const clients = clientsResponse?.items || [];

  // Buscar nome do canal selecionado
  const selectedChannelName = useMemo(() => {
    if (!channels || !selectedChannelId) return null;
    const channel = channels.find((c: any) => c.id === selectedChannelId);
    return channel?.name || null;
  }, [channels, selectedChannelId]);

  // Manter o nome do cliente selecionado resolvido mesmo que ele saia do lote
  // atual (ex.: usuário digitou uma nova busca sem trocar a seleção).
  useEffect(() => {
    if (!selectedClientId) return;
    const client = clients.find((c: any) => c.id === selectedClientId);
    if (client) setSelectedClientName(client.nomeFantasia);
  }, [selectedClientId, clients]);

  // Auto-selecionar canal se clientId veio da URL (Admin Sálvia)
  useEffect(() => {
    if (isAdminSalvia && clientIdFromUrl && clients.length > 0 && !channelIdFromUrl) {
      const client = clients.find((c: any) => c.id === Number(clientIdFromUrl));
      if (client) {
        setSelectedChannelId(client.channelId);
        setChannelLocked(true);
      }
    }
  }, [isAdminSalvia, clientIdFromUrl, channelIdFromUrl, clients]);

  // Mutation para criar campanha
  const createMutation = trpc.questionnaire.campaign.createDraft.useMutation({
    onSuccess: (data) => {
      sonnerToast.success("Campanha Criada", {
        description: "Campanha criada com sucesso! Agora você pode ativá-la.",
      });
      // Voltar para lista mantendo filtros
      const params = new URLSearchParams();
      if (selectedChannelId) params.set("channelId", selectedChannelId.toString());
      if (selectedClientId) params.set("clientId", selectedClientId.toString());
      setLocation("/questionarios");
    },
    onError: (error) => {
      let errorMessage = "Erro ao criar campanha. Tente novamente.";
      
      if (error.message.includes("Data de encerramento inválida")) {
        errorMessage = "Data de encerramento inválida. Selecione uma data futura.";
      } else if (error.message.includes("Empresa não encontrada")) {
        errorMessage = "Cliente não encontrado. Selecione um cliente válido.";
      } else if (error.message && !error.message.includes("{")) {
        errorMessage = error.message;
      }
      
      console.error("[createDraft error]", error);
      
      sonnerToast.error("Erro ao Criar", {
        description: errorMessage,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (isAdminSalvia && !selectedChannelId) {
      sonnerToast.error("Canal Obrigatório", {
        description: "Selecione um canal para criar a campanha.",
      });
      return;
    }

    if (!selectedClientId) {
      sonnerToast.error("Cliente Obrigatório", {
        description: "Selecione um cliente para criar a campanha.",
      });
      return;
    }

    if (!endsAt) {
      sonnerToast.error("Data Obrigatória", {
        description: "Selecione a data de encerramento da campanha.",
      });
      return;
    }

    if (kMin < 1) {
      sonnerToast.error("kMin Inválido", {
        description: "O valor deve ser maior ou igual a 1.",
      });
      return;
    }

    // Converter endsAt para ISO 8601 com horário final do dia (23:59:59.999)
    const endsAtDate = new Date(endsAt);
    endsAtDate.setHours(23, 59, 59, 999);
    const endsAtISO = endsAtDate.toISOString();

    createMutation.mutate({
      companyId: selectedClientId,
      endsAt: endsAtISO,
      kMin,
      consolidationMode: "FINAL_ONLY",
      allowMultipleResponsesPerDevice,
    });
  };

  // Verificar se o formulário está válido
  const isFormValid = useMemo(() => {
    if (isAdminSalvia && !selectedChannelId) return false;
    if (!selectedClientId) return false;
    if (!endsAt) return false;
    return true;
  }, [isAdminSalvia, selectedChannelId, selectedClientId, endsAt]);

  return (
    <SalviaLayout title="Nova Campanha de Questionário">
      <div className="container mx-auto py-8 max-w-3xl">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => setLocation("/questionarios")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Questionários
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Criar Nova Campanha de Questionário</CardTitle>
            <CardDescription>
              Preencha os dados para criar uma campanha de aplicação do questionário ERGOS
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Seleção de Canal (Admin Sálvia) */}
              {isAdminSalvia && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="channel">Canal (Parceiro) *</Label>
                    {channelLocked && selectedChannelId && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setChannelLocked(false);
                          setClientLocked(false);
                          setSelectedClientId(null);
                        }}
                        className="text-xs h-6 px-2"
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Trocar
                      </Button>
                    )}
                  </div>
                  
                  {channelLocked && selectedChannelId ? (
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                      <Lock className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{selectedChannelName || `Canal #${selectedChannelId}`}</span>
                      <Badge variant="secondary" className="ml-auto">Pré-selecionado</Badge>
                    </div>
                  ) : loadingChannels ? (
                    <div className="text-sm text-muted-foreground py-2">Carregando canais...</div>
                  ) : (
                    <Popover open={channelOpen} onOpenChange={setChannelOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={channelOpen}
                          className="w-full justify-between font-normal"
                          type="button"
                        >
                          {selectedChannelId
                            ? selectedChannelName || `Canal #${selectedChannelId}`
                            : "Selecione um canal..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command
                          filter={(value, search) => {
                            if (!search) return 1;
                            const normalizedSearch = removeAccents(search.toLowerCase());
                            const normalizedValue = removeAccents(value.toLowerCase());
                            return normalizedValue.includes(normalizedSearch) ? 1 : 0;
                          }}
                        >
                          <CommandInput placeholder="Digite para buscar canal..." />
                          <CommandList className="max-h-[300px]">
                            <CommandEmpty>Nenhum canal encontrado</CommandEmpty>
                            <CommandGroup>
                              {channels?.map((channel: any) => (
                                <CommandItem
                                  key={channel.id}
                                  value={channel.name}
                                  onSelect={() => {
                                    const newId = channel.id;
                                    if (newId !== selectedChannelId) {
                                      setSelectedChannelId(newId);
                                      setSelectedClientId(null);
                                      setSelectedClientName(null);
                                      setClientSearchInput("");
                                      setClientLocked(false);
                                    }
                                    setChannelOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedChannelId === channel.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {channel.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}

              {/* Seleção de Cliente */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="client">Cliente *</Label>
                  {clientLocked && selectedClientId && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setClientLocked(false)}
                      className="text-xs h-6 px-2"
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Trocar
                    </Button>
                  )}
                </div>

                {clientLocked && selectedClientId ? (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                    <Lock className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{selectedClientName || `Cliente #${selectedClientId}`}</span>
                    <Badge variant="secondary" className="ml-auto">Pré-selecionado</Badge>
                  </div>
                ) : isAdminSalvia && !selectedChannelId ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>Selecione um canal para visualizar os clientes.</AlertDescription>
                  </Alert>
                ) : !loadingClients && clients.length === 0 && !clientSearchDebounced ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>Nenhum cliente encontrado</AlertDescription>
                  </Alert>
                ) : (
                  <Popover open={clientOpen} onOpenChange={setClientOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={clientOpen}
                        className="w-full justify-between font-normal"
                        type="button"
                        disabled={isAdminSalvia && !selectedChannelId}
                      >
                        {selectedClientId
                          ? selectedClientName
                            ? `${selectedClientName}`
                            : `Cliente #${selectedClientId}`
                          : "Selecione um cliente..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      {/* shouldFilter=false: a busca já é feita no backend (trpc.clients.list
                          com `search`), então não refiltramos client-side por cima do resultado. */}
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Digite para buscar cliente..."
                          value={clientSearchInput}
                          onValueChange={setClientSearchInput}
                        />
                        <CommandList className="max-h-[300px]">
                          {loadingClients ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              Buscando clientes...
                            </div>
                          ) : (
                            <>
                              <CommandEmpty>Nenhum cliente encontrado</CommandEmpty>
                              <CommandGroup>
                                {clients.map((client: any) => (
                                  <CommandItem
                                    key={client.id}
                                    value={`${client.nomeFantasia} ${client.cnpj}`}
                                    onSelect={() => {
                                      setSelectedClientId(client.id);
                                      setSelectedClientName(client.nomeFantasia);
                                      setClientOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        selectedClientId === client.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span>{client.nomeFantasia}</span>
                                      <span className="text-xs text-muted-foreground">{client.cnpj}</span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>

              {/* kMin */}
              <div className="space-y-2">
                <Label htmlFor="kMin">kMin (Mínimo de Respostas para Anonimato) *</Label>
                <Input
                  id="kMin"
                  type="number"
                  min={1}
                  max={100}
                  value={kMin}
                  onChange={(e) => setKMin(Number(e.target.value))}
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Mínimo: 1. Valor padrão: 1. Define o mínimo de respostas por setor/cargo para liberar o resultado. Use 1 para campanhas individuais ou valores maiores para garantir anonimato coletivo.
                </p>
              </div>

              {/* Data de Encerramento */}
              <div className="space-y-2">
                <Label htmlFor="endsAt">Data de Encerramento *</Label>
                <Input
                  id="endsAt"
                  type="date"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Data limite para os colaboradores responderem ao questionário.
                </p>
              </div>

              {/* Bloquear Múltiplas Respostas */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <input
                    id="blockMultiple"
                    type="checkbox"
                    checked={!allowMultipleResponsesPerDevice}
                    onChange={(e) => setAllowMultipleResponsesPerDevice(!e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="blockMultiple" className="cursor-pointer font-normal">
                    Bloquear múltiplas respostas no mesmo dispositivo
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground ml-7">
                  {!allowMultipleResponsesPerDevice
                    ? "✅ Ativado (padrão): Cada dispositivo pode responder apenas uma vez."
                    : "⚠️ Desativado: Permite múltiplas respostas do mesmo dispositivo (use com cautela)."}
                </p>
              </div>

              {/* Template (Read-only) */}
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  <strong>Template do Questionário:</strong> Última versão PUBLISHED do template ERGOS será
                  congelada ao ativar a campanha.
                </AlertDescription>
              </Alert>

              {/* Botões */}
              <div className="flex items-center gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={createMutation.isPending || !isFormValid}
                  className="flex-1"
                >
                  {createMutation.isPending ? "Criando..." : "Criar Campanha (Rascunho)"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation("/questionarios")}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </SalviaLayout>
  );
}
